'use client';

import { MediaCarousel } from '@/components/media-carousel/media-carousel';
import { useTheaterContext } from '@/hooks/state/theater';
import { movieToMoviePoster } from '@/utils/movie-helper';
import { Movie } from '@cinemark/content-api-client';
import { useMemo } from 'react';
import { MovieMediaItem, MovieMediaItemComponent, stillImageToMovieMediaItem } from './movie-media-item';

type MovieMediaItemWithId = MovieMediaItem & { uniqueId: string; };

type MovieMediaCarouselComponentProps = {
  dataLinkCategory: string;
  movie: Movie;
  title?: string;
};

const SCROLL_BY_SIZE = 1;

const getMovieMediaItems = (movie: Movie): MovieMediaItemWithId[] => {
  let mediaItems: MovieMediaItemWithId[] = [];
  let itemCounter = 0;

  if (movie.trailers?.length) {
    mediaItems = (movie.trailers.map((trailer) => {
      if (!trailer.thumbnailUrl && !trailer.thumbnailHighResUrl) return null;

      return {
        largeImageUrl: trailer.thumbnailHighResUrl || trailer.thumbnailUrl,
        smallImageUrl: trailer.thumbnailUrl,
        trailer,
        uniqueId: `trailer-${itemCounter++}`,
      };
    }).filter(Boolean)) as MovieMediaItemWithId[];
  }

  if (movie.stillImagesLarge?.length) {
    const stillImages = stillImageToMovieMediaItem(movie.stillImagesLarge).map(item => ({
      ...item,
      uniqueId: `still-${itemCounter++}`,
    }));
    mediaItems = mediaItems.concat(stillImages);
  }

  const seenImageUrls = new Set<string>(
    mediaItems
      .filter(item => item.trailer)
      .flatMap(item => [item.largeImageUrl, item.smallImageUrl])
      .filter((url): url is string => !!url)
  );

  return mediaItems.filter((item) => {
    if (item.trailer) return true;

    const url = item.largeImageUrl || item.smallImageUrl;
    if (!url || seenImageUrls.has(url)) return false;

    seenImageUrls.add(url);
    return true;
  });
};

export const MovieMediaCarouselComponent = ({
  dataLinkCategory,
  movie,
  title,
}: MovieMediaCarouselComponentProps): React.JSX.Element | null => {
  const { data: theater } = useTheaterContext();
  const items = useMemo(() => getMovieMediaItems(movie), [movie]);

  const displayItem = (item: MovieMediaItemWithId, index: number): React.JSX.Element => {
    return (
      <MovieMediaItemComponent dataLinkCategory={dataLinkCategory}
        dataLinkName={`Movie Still ${index + 1}`}
        largeImageUrl={item.largeImageUrl}
        moviePoster={movieToMoviePoster(movie, dataLinkCategory, theater)}
        movieTitle={movie.movieTitle || ''}
        smallImageUrl={item.smallImageUrl}
        theater={theater}
        trailer={item.trailer}
        trailerDataLinkName="Play Trailer"
      />
    );
  };

  if (!items?.length) return null;

  return (
    <div className="min-w-0 flex-1 max-w-[860px] flex flex-col gap-ms">
      {!!title && <h4 data-test="media-title">{title}</h4>}

      <div className="w-full">
        <MediaCarousel ariaLabel="Movie media carousel"
          carouselContentClassName="items-stretch gap-0"
          carouselOptions={{
            align: 'center',
            containScroll: 'keepSnaps',
            slidesToScroll: SCROLL_BY_SIZE,
            watchDrag: true,
            watchResize: true,
          }}
          desktopNextButtonClassName="size-12 -right-1 hidden md:flex"
          desktopPreviousButtonClassName="size-12 left-1 hidden md:flex"
          dotClassName={isSelected => `size-[8px] min-h-0 min-w-0 rounded-full ${isSelected ? 'bg-white' : 'bg-gray-400'}`}
          dotDataTest={index => `button-movie-media-carousel-indicator-${index + 1}`}
          itemKey={item => item.uniqueId}
          items={items}
          nextButtonDataTest="button-movie-media-carousel-next"
          previousButtonDataTest="button-movie-media-carousel-previous"
          renderItem={displayItem}
          slideClassName="w-[375px] h-[211px] desktop-xs:basis-[600px] desktop-xs:w-[600px] desktop-xs:h-[338px] md:basis-full md:max-w-[800px] md:h-auto"
        />
      </div>
    </div>
  );
};
